import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import VisualizadorDocumentosModal from './VisualizadorDocumentosModal.jsx';
import { 
  X, 
  RotateCcw, 
  FileText, 
  CheckCircle2, 
  PhoneCall, 
  Edit3,
  ArrowRight,
  ShieldCheck,
  Layers,
  Upload,
  Eye,
  Download,
  Trash2,
  Paperclip,
  AlertCircle
} from 'lucide-react';

const MOTIVOS_PREDETERMINADOS = [
  'Llamada telefónica del requirente informando error al ingresar en la web',
  'Error de clasificación inicial del requirente en el formulario web',
  'Reclasificación legal del acto por el funcionario de mesón',
  'Subsanación administrativa y aporte de nuevos documentos',
];

const BASE_API_URL = (import.meta.env.VITE_API_URL || 'https://cbrsancarlos.cl').replace(/\/+$/, '');

export default function RectificarTramiteModal({ tramite, onClose, onSuccess }) {
  const [areas, setAreas] = useState([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [selectedTipoId, setSelectedTipoId] = useState('');
  const [motivo, setMotivo] = useState(MOTIVOS_PREDETERMINADOS[0]);
  const [observaciones, setObservaciones] = useState(tramite?.observaciones || '');

  // Documentos
  const [documentosExistentes, setDocumentosExistentes] = useState(tramite?.documentos || []);
  const [nuevosArchivos, setNuevosArchivos] = useState([]);
  const [docVisualizar, setDocVisualizar] = useState(null);
  const fileInputRef = useRef(null);

  // Cargar áreas, tipos y documentos frescos del trámite
  useEffect(() => {
    let isMounted = true;
    setLoadingAreas(true);

    Promise.all([
      api.get('/tramites/areas'),
      api.get(`/tramites/${tramite.id}`).catch(() => ({ data: tramite }))
    ])
      .then(([resAreas, resTramite]) => {
        if (!isMounted) return;
        const areasData = Array.isArray(resAreas.data) ? resAreas.data : [];
        setAreas(areasData);

        const tramiteData = resTramite.data || tramite;
        if (tramiteData.documentos) {
          setDocumentosExistentes(tramiteData.documentos);
        }

        if (tramiteData.tipo_inscripcion_id) {
          setSelectedTipoId(String(tramiteData.tipo_inscripcion_id));
        }
        if (tramiteData.area_id) {
          setSelectedAreaId(String(tramiteData.area_id));
        } else if (tramiteData.tipo_inscripcion?.area_id) {
          setSelectedAreaId(String(tramiteData.tipo_inscripcion.area_id));
        }
      })
      .catch(err => {
        console.error('Error al cargar datos del modal:', err);
        toast.error('No se pudieron cargar todos los datos del trámite');
      })
      .finally(() => {
        if (isMounted) setLoadingAreas(false);
      });

    return () => {
      isMounted = false;
    };
  }, [tramite]);

  // Clasificación de áreas
  const areasPrincipales = areas.filter(a => a.tipo_inscripciones && a.tipo_inscripciones.length > 0);
  const subAreas = areas.filter(a => !a.tipo_inscripciones || a.tipo_inscripciones.length === 0);

  const todosLosTipos = areasPrincipales.flatMap(a => (a.tipo_inscripciones || []).map(t => ({ ...t, area_nombre: a.nombre, area_id: a.id })));
  const tipoActual = todosLosTipos.find(t => String(t.id) === String(tramite?.tipo_inscripcion_id)) || tramite?.tipo_inscripcion;
  const nuevoTipo = todosLosTipos.find(t => String(t.id) === String(selectedTipoId));

  // Determinar si el tipo seleccionado exige submenú de registro específico
  const requiereRegistro = Boolean(nuevoTipo?.requiere_registro);

  // Manejador al cambiar el Tipo
  const handleTipoChange = (e) => {
    const val = e.target.value;
    setSelectedTipoId(val);

    const tipoObj = todosLosTipos.find(t => String(t.id) === String(val));
    if (tipoObj) {
      if (tipoObj.requiere_registro) {
        if (!selectedAreaId || !subAreas.some(s => String(s.id) === String(selectedAreaId))) {
          setSelectedAreaId(subAreas.length > 0 ? String(subAreas[0].id) : '');
        }
      } else {
        setSelectedAreaId(String(tipoObj.area_id));
      }
    }
  };

  // Manejador de nuevos archivos
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Validación de tamaño individual (máx 25 MB)
    const validos = [];
    for (const f of files) {
      if (f.size > 26214400) {
        toast.error(`El archivo "${f.name}" excede el límite de 25 MB`);
      } else {
        validos.push(f);
      }
    }

    setNuevosArchivos(prev => [...prev, ...validos]);
    // Limpiar input para permitir seleccionar el mismo archivo si fue eliminado
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveNuevoArchivo = (index) => {
    setNuevosArchivos(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Previsualización de área destino
  const areaDestino = subAreas.find(a => String(a.id) === String(selectedAreaId)) 
    || areas.find(a => String(a.id) === String(selectedAreaId)) 
    || nuevoTipo?.area;

  // Comprobar si hay cambios efectivos
  const hayCambioTipo = selectedTipoId && String(selectedTipoId) !== String(tramite?.tipo_inscripcion_id);
  const hayCambioArea = selectedAreaId && String(selectedAreaId) !== String(tramite?.area_id);
  const hayNuevosArchivos = nuevosArchivos.length > 0;
  const hayCambio = hayCambioTipo || hayCambioArea || hayNuevosArchivos;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTipoId) {
      toast.error('Por favor selecciona el nuevo tipo de trámite');
      return;
    }

    if (requiereRegistro && !selectedAreaId) {
      toast.error('Debes seleccionar el Registro o Área Requerida en el submenú');
      return;
    }

    if (!motivo.trim()) {
      toast.error('Debes indicar el motivo de la rectificación');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      formData.append('tipo_inscripcion_id', selectedTipoId);
      if (selectedAreaId) {
        formData.append('area_id', selectedAreaId);
      }
      formData.append('motivo', motivo.trim());
      if (observaciones.trim()) {
        formData.append('observaciones', observaciones.trim());
      }

      nuevosArchivos.forEach(file => {
        formData.append('documentos[]', file);
      });

      const res = await api.post(`/tramites/${tramite.id}/rectificar-tipo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      toast.success(res.data?.message || 'Trámite rectificado y subsanado exitosamente');
      if (onSuccess) {
        onSuccess(res.data.tramite);
      }
      onClose();
    } catch (err) {
      console.error('Error al rectificar trámite:', err);
      const errMsg = err.response?.data?.error || err.response?.data?.message || 'No fue posible rectificar el trámite';
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.65)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '1rem',
      animation: 'fadeIn 0.2s ease-out'
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '720px',
        maxHeight: '92vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        border: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          background: 'linear-gradient(to right, #f8fafc, #eff6ff)',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                background: '#ea580c',
                color: '#ffffff',
                padding: '6px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Edit3 size={18} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
                Rectificación y Subsanación del Trámite
              </h3>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
              Modifica el tipo de acto, submenú de registro y aporta nuevos documentos ante llamados o subsanaciones del requirente.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '6px',
              borderRadius: '8px',
              cursor: 'pointer',
              color: '#94a3b8',
              display: 'flex'
            }}
            title="Cerrar modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          
          {/* Identificación del Trámite */}
          <div style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '10px 14px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            fontSize: '0.82rem'
          }}>
            <div>
              <span style={{ color: '#64748b', fontWeight: 600 }}>Folio:</span>{' '}
              <strong style={{ fontFamily: 'monospace', color: '#0f172a', fontSize: '0.9rem' }}>
                {tramite.numero_seguimiento}
              </strong>
            </div>
            <div>
              <span style={{ color: '#64748b', fontWeight: 600 }}>Solicitante:</span>{' '}
              <strong style={{ color: '#0f172a' }}>{tramite.nombre_solicitante}</strong>{' '}
              <span style={{ color: '#94a3b8' }}>({tramite.rut_solicitante})</span>
            </div>
            <div>
              <span style={{ color: '#64748b', fontWeight: 600 }}>Estado:</span>{' '}
              <span style={{
                backgroundColor: '#eff6ff',
                color: '#1d4ed8',
                padding: '2px 8px',
                borderRadius: '9999px',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}>
                {tramite.estado?.nombre || 'Ingresado'}
              </span>
            </div>
          </div>

          {/* Comparador Visual: Tipo Anterior vs Nuevo Tipo */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '12px',
            alignItems: 'center',
            backgroundColor: '#f1f5f9',
            padding: '14px',
            borderRadius: '12px',
            border: '1px solid #cbd5e1'
          }}>
            {/* Tarjeta Actual */}
            <div style={{
              backgroundColor: '#ffffff',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>
                Tipo Actual Registrado
              </span>
              <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>
                {tipoActual?.nombre || tramite?.tipo_inscripcion?.nombre || 'Inscripción'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>
                Registro/Área: <strong>{tramite?.area?.nombre || tipoActual?.area_nombre || 'General'}</strong>
              </div>
            </div>

            {/* Flecha Central */}
            <div style={{ display: 'flex', justifyContent: 'center', color: '#64748b' }}>
              <ArrowRight size={22} />
            </div>

            {/* Tarjeta Nueva */}
            <div style={{
              backgroundColor: hayCambio ? '#f0fdf4' : '#ffffff',
              padding: '10px 12px',
              borderRadius: '8px',
              border: hayCambio ? '1px solid #86efac' : '1px dashed #cbd5e1'
            }}>
              <span style={{ 
                fontSize: '0.7rem', 
                fontWeight: 700, 
                color: hayCambio ? '#15803d' : '#64748b', 
                textTransform: 'uppercase', 
                display: 'block', 
                marginBottom: 2 
              }}>
                {hayCambio ? '✨ Rectificación a Aplicar' : 'Nuevo Tipo Seleccionado'}
              </span>
              <div style={{ fontWeight: 600, fontSize: '0.88rem', color: hayCambio ? '#166534' : '#64748b' }}>
                {nuevoTipo?.nombre || 'Seleccione un tipo abajo...'}
              </div>
              <div style={{ fontSize: '0.75rem', color: hayCambio ? '#15803d' : '#94a3b8', marginTop: 4 }}>
                Registro/Área: <strong>{areaDestino?.nombre || '—'}</strong>
              </div>
            </div>
          </div>

          {/* Selector de Nuevo Tipo de Trámite */}
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
              Seleccionar Nuevo Tipo de Trámite / Acto <span style={{ color: '#ef4444' }}>*</span>
            </label>
            {loadingAreas ? (
              <div style={{ padding: '8px 12px', color: '#64748b', fontSize: '0.85rem', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                Cargando catálogo de trámites...
              </div>
            ) : (
              <select
                value={selectedTipoId}
                onChange={handleTipoChange}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.88rem',
                  color: '#0f172a',
                  backgroundColor: '#ffffff',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">-- Selecciona el tipo de trámite corregido --</option>
                {areasPrincipales.map(area => (
                  <optgroup key={area.id} label={`📂 ${area.nombre}`}>
                    {(area.tipo_inscripciones || []).map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
          </div>

          {/* SUBMENÚ: Registro o Área Requerida (Condicional cuando requiere_registro === true) */}
          {requiereRegistro && (
            <div style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '10px',
              padding: '14px',
              borderLeft: '4px solid #f59e0b',
              animation: 'fadeIn 0.25s ease-out'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 6 }}>
                <Layers size={16} color="#d97706" />
                <label style={{ fontSize: '0.88rem', fontWeight: 700, color: '#92400e', margin: 0 }}>
                  Submenú: Registro o Área Específica Requerida <span style={{ color: '#ef4444' }}>*</span>
                </label>
              </div>
              <p style={{ margin: '0 0 8px', fontSize: '0.78rem', color: '#b45309' }}>
                Para <strong>{nuevoTipo?.nombre}</strong> es obligatorio indicar en cuál de los registros del Conservador se asentará la inscripción.
              </p>
              <select
                value={selectedAreaId}
                onChange={e => setSelectedAreaId(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '7px',
                  border: '1px solid #f59e0b',
                  fontSize: '0.88rem',
                  color: '#0f172a',
                  backgroundColor: '#ffffff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="">-- Seleccione el Registro Específico --</option>
                {subAreas.map(sub => (
                  <option key={sub.id} value={sub.id}>
                    Registro de {sub.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* SECCIÓN DOCUMENTAL: Ver Documentos Actuales y Aportar Nuevos */}
          <div style={{
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Paperclip size={16} color="#0284c7" />
                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
                  Expediente Documental y Subsanación
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {documentosExistentes.length} archivo(s) registrado(s)
                </span>
                {documentosExistentes.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setDocVisualizar(documentosExistentes[0])}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: '6px',
                      backgroundColor: '#e0f2fe',
                      color: '#0369a1',
                      border: '1px solid #bae6fd',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                    title="Abrir visualizador de documentos del trámite"
                  >
                    <Eye size={12} /> Abrir Visor
                  </button>
                )}
              </div>
            </div>

            {/* Lista de Documentos Existentes */}
            {documentosExistentes.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>
                  Documentos Actuales del Expediente:
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '140px', overflowY: 'auto' }}>
                  {documentosExistentes.map((doc, idx) => {
                    const urlVer = `${BASE_API_URL}/api/v1/tramites/documentos/${doc.token_descarga}`;
                    const urlDescargar = `${BASE_API_URL}/api/v1/tramites/documentos/${doc.token_descarga}/descargar`;
                    return (
                      <div
                        key={doc.id || idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '6px 10px',
                          backgroundColor: '#ffffff',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.8rem'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
                          <FileText size={14} color="#64748b" style={{ flexShrink: 0 }} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#334155' }} title={doc.nombre_original}>
                            {doc.nombre_original}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <button
                            type="button"
                            onClick={() => setDocVisualizar(doc)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '3px 8px',
                              borderRadius: '4px',
                              backgroundColor: '#eff6ff',
                              color: '#1d4ed8',
                              border: '1px solid #bfdbfe',
                              fontSize: '0.74rem',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                            title="Abrir visor de documentos"
                          >
                            <Eye size={13} /> Ver
                          </button>
                          <a
                            href={urlDescargar}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                              padding: '2px 7px',
                              borderRadius: '4px',
                              backgroundColor: '#f1f5f9',
                              color: '#475569',
                              border: '1px solid #cbd5e1',
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              textDecoration: 'none'
                            }}
                            title="Descargar archivo adjunto"
                          >
                            <Download size={12} /> Descargar
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic', padding: '6px 8px', background: '#ffffff', borderRadius: 6, border: '1px dashed #cbd5e1' }}>
                No hay documentos adjuntos previamente en este trámite.
              </div>
            )}

            {/* Documentos sugeridos para el tipo seleccionado */}
            {nuevoTipo?.documentos_requeridos && nuevoTipo.documentos_requeridos.length > 0 && (
              <div style={{
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '0.78rem',
                color: '#1e40af'
              }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                  📌 Documentos sugeridos para {nuevoTipo.nombre}:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                  {nuevoTipo.documentos_requeridos.map((req, rIdx) => (
                    <li key={rIdx}>{req}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Input y Zona para Adjuntar Nuevos Documentos */}
            <div>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', display: 'block', marginBottom: 4 }}>
                Subsanar / Aportar Nuevos Documentos (PDF, Imagen, Word):
              </span>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                style={{ display: 'none' }}
                id="rectificar-file-input"
              />
              <label
                htmlFor="rectificar-file-input"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '2px dashed #93c5fd',
                  backgroundColor: '#ffffff',
                  color: '#1d4ed8',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ffffff'}
              >
                <Upload size={16} />
                Haz clic aquí para seleccionar o adjuntar nuevos archivos de subsanación
              </label>

              {/* Lista de Nuevos Archivos Seleccionados */}
              {nuevosArchivos.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.74rem', fontWeight: 600, color: '#166534' }}>
                    Archivos a adjuntar al guardar ({nuevosArchivos.length}):
                  </span>
                  {nuevosArchivos.map((file, fIdx) => (
                    <div
                      key={fIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '5px 10px',
                        backgroundColor: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '6px',
                        fontSize: '0.78rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <FileText size={13} color="#166534" />
                        <span style={{ color: '#14532d', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </span>
                        <span style={{ color: '#86efac', fontSize: '0.7rem' }}>
                          ({formatFileSize(file.size)})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNuevoArchivo(fIdx)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Quitar este archivo"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Motivo de la Rectificación */}
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
              Motivo o Justificación de la Rectificación <span style={{ color: '#ef4444' }}>*</span>
            </label>
            
            {/* Botones de sugerencia rápida */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: 8 }}>
              {MOTIVOS_PREDETERMINADOS.map((sug, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setMotivo(sug)}
                  style={{
                    fontSize: '0.72rem',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    border: motivo === sug ? '1px solid #ea580c' : '1px solid #e2e8f0',
                    backgroundColor: motivo === sug ? '#fff7ed' : '#f8fafc',
                    color: motivo === sug ? '#c2410c' : '#475569',
                    cursor: 'pointer',
                    fontWeight: motivo === sug ? 600 : 400,
                    textAlign: 'left'
                  }}
                >
                  {i === 0 && <PhoneCall size={11} style={{ display: 'inline', marginRight: 4 }} />}
                  {sug}
                </button>
              ))}
            </div>

            <textarea
              rows={2}
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Explica brevemente la razón del cambio (ej: Solicitante llamó indicando error al seleccionar el tipo en el sitio web)..."
              required
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.85rem',
                color: '#0f172a',
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Observaciones del Trámite (Opcional) */}
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: 6 }}>
              Observaciones del Trámite (Opcional)
            </label>
            <textarea
              rows={2}
              value={observaciones}
              onChange={e => setObservaciones(e.target.value)}
              placeholder="Notas u observaciones que deben acompañar al trámite..."
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.85rem',
                color: '#0f172a',
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Aviso de Registro en Workflow Steps */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: '10px 12px',
            backgroundColor: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            fontSize: '0.78rem',
            color: '#1e40af'
          }}>
            <ShieldCheck size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <strong>Auditoría Transparente:</strong> Este cambio quedará registrado automáticamente en los <em>Workflow Steps</em> (Historial del Trámite) indicando fecha, hora, tipo anterior, nuevo tipo, registro/área, nuevos documentos adjuntos y motivo.
            </div>
          </div>

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            paddingTop: '0.75rem',
            borderTop: '1px solid #e2e8f0',
            marginTop: '0.5rem'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontSize: '0.88rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={saving || !hayCambio}
              style={{
                padding: '8px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: hayCambio ? '#ea580c' : '#94a3b8',
                color: '#ffffff',
                fontSize: '0.88rem',
                fontWeight: 600,
                cursor: hayCambio && !saving ? 'pointer' : 'not-allowed',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: hayCambio ? '0 2px 4px rgba(234, 88, 12, 0.3)' : 'none'
              }}
            >
              {saving ? (
                <>
                  <RotateCcw size={15} className="spinner" /> Guardando...
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} /> Confirmar Rectificación y Subsanar
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Visualizador Bonito de Documentos del Trámite */}
      {docVisualizar && (
        <VisualizadorDocumentosModal
          isOpen={Boolean(docVisualizar)}
          onClose={() => setDocVisualizar(null)}
          tramite={{
            ...tramite,
            documentos: documentosExistentes
          }}
          initialDoc={typeof docVisualizar === 'object' ? docVisualizar : null}
          zIndex={100010}
        />
      )}
    </div>
  );
}
